import {
  formatDateRange,
  getActiveEducation,
  getActiveExperiences,
  getExperienceTechnologies,
  getLanguages,
  getAboutText,
  getObjectiveText,
  getPrimaryContactItems,
  getProjects,
  getSkills,
  getSocialItems,
  getSummaryText,
  hasText,
  stripHtml,
} from "./resumeTemplateUtils.js";

const KNOWN_FIELDS = new Set([
  "name",
  "role",
  "roleTitle",
  "email",
  "phone",
  "location",
  "linkedin",
  "github",
  "photo",
  "summary",
  "objective",
  "relevantExperience",
  "totalExperience",
  "skill",
  "language",
  "hobby",
  "reference",
  "social",
  "certification",
  "achievements",
  "company",
  "jobRole",
  "description",
  "years",
  "degree",
  "institution",
  "field",
  "score",
  "board",
  "project",
  "startDate",
  "endDate",
  "subtitle",
  "technology",
  "skillCount",
  "languageCount",
  "projectCount",
]);

const DUPLICATE_SECTION_KINDS = new Set([
  "contact",
  "contactgrid",
  "social",
  "links",
  "skills",
  "languages",
  "summary",
  "objective",
  "profile",
  "header",
  "highlights",
  "highlight",
  "voiceprofile",
]);

const PLACEHOLDER_VALUE_REGEX = /^(?:test|n\/a|na|null|undefined|sdfghj|jhgjh|asdfg?h?|qwerty|lorem\s+ipsum)$/i;
const FORBIDDEN_SECTION_LABELS = new Set([
  "snapshot",
  "metrics",
  "contact grid",
  "connect",
  "contactgrid",
  "range",
  "voice profile",
  "voiceprofile",
]);

const FORBIDDEN_SECTION_KINDS = new Set([
  "metrics",
  "snapshot",
  "contactgrid",
  "voiceprofile",
]);

const FORBIDDEN_FIELDS = new Set([
  "relevantExperience",
  "totalExperience",
  "skillCount",
  "languageCount",
  "projectCount",
]);

const PRESERVE_RENDERED_FALLBACK_FIELDS = new Set([
  "skill",
  "language",
  "hobby",
  "reference",
  "social",
  "certification",
  "description",
  "achievements",
  "technology",
]);

function normalizeValue(value) {
  return String(value || "")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function hasRenderableText(value) {
  const normalized = normalizeValue(value);
  return hasText(normalized) && !PLACEHOLDER_VALUE_REGEX.test(normalized);
}

function isNodeVisible(node) {
  return Boolean(node) && node.getAttribute("data-cv-hidden") !== "true";
}

function hideNode(node, reason = "") {
  if (!node) return;

  node.setAttribute("data-cv-hidden", "true");
  if (reason) {
    node.setAttribute("data-cv-hidden-reason", reason);
  }
  node.style.display = "none";
}

function showNode(node) {
  if (!node) return;

  node.removeAttribute("data-cv-hidden");
  node.removeAttribute("data-cv-hidden-reason");
  node.style.display = "";
}

function hideSingleFieldShell(node, reason = "") {
  let current = node?.parentElement || null;

  while (current && !current.hasAttribute("data-resume-root")) {
    const computedStyle = current.ownerDocument?.defaultView?.getComputedStyle?.(current);
    const layoutDisplay = (computedStyle?.display || "").toLowerCase();
    const hasExplicitGridTracks =
      Boolean(current.style?.gridTemplateColumns) ||
      Boolean(current.style?.gridTemplateRows);
    const hasMultipleVisibleChildren = Array.from(current.children || []).filter(
      (child) => isNodeVisible(child),
    ).length > 1;

    if (
      layoutDisplay.includes("grid") ||
      layoutDisplay.includes("flex") ||
      hasExplicitGridTracks ||
      hasMultipleVisibleChildren ||
      current.hasAttribute("data-section") ||
      current.hasAttribute("data-cv-group") ||
      current.hasAttribute("data-cv-section")
    ) {
      current = current.parentElement;
      continue;
    }

    const fieldNodes = Array.from(current.querySelectorAll("[data-field]"));

    if (fieldNodes.length === 1 && fieldNodes[0] === node) {
      hideNode(current, reason);
      return;
    }

    current = current.parentElement;
  }
}

function getNodeCollectionIndex(node, selector) {
  const scopeRoot =
    node.closest("[data-section='experienceItem'], [data-section='projectItem'], [data-section='educationItem'], [data-section='education'], [data-section='skills'], [data-section='languages'], [data-section='achievements'], [data-section='recognition'], [data-section='awards']") ||
    node.ownerDocument;
  const nodes = Array.from(scopeRoot.querySelectorAll(selector));
  return nodes.indexOf(node);
}

function getExperienceIndex(node, root) {
  const itemNode = node.closest("[data-section='experienceItem']");
  if (!itemNode) return -1;

  const items = Array.from(root.querySelectorAll("[data-section='experienceItem']"));
  return items.indexOf(itemNode);
}

function getEducationIndex(node, root) {
  const itemNode = node.closest("[data-section='educationItem']");

  if (itemNode) {
    const items = Array.from(root.querySelectorAll("[data-section='educationItem']"));
    return items.indexOf(itemNode);
  }

  const sectionNode = node.closest("[data-section='education']");
  if (!sectionNode) return -1;

  const sectionItems = Array.from(root.querySelectorAll("[data-section='education']"))
    .filter((section) => !section.querySelector("[data-section='educationItem']"));
  const sectionIndex = sectionItems.indexOf(sectionNode);

  return sectionIndex === -1 ? 0 : sectionIndex;
}

function getProjectIndex(node, root) {
  const itemNode = node.closest("[data-section='projectItem']");
  if (!itemNode) return -1;

  const items = Array.from(root.querySelectorAll("[data-section='projectItem']"));
  return items.indexOf(itemNode);
}

function getRenderableSnapshot(data) {
  const sanitizedData = Object.fromEntries(
    Object.entries(data || {}).map(([key, value]) => {
      if (typeof value !== "string") return [key, value];

      const normalized = normalizeValue(value);
      return [key, PLACEHOLDER_VALUE_REGEX.test(normalized) ? "" : normalized];
    }),
  );
  const normalizedName = normalizeValue(sanitizedData.name);

  if (normalizedName) {
    Object.keys(sanitizedData).forEach((key) => {
      if (key === "name") return;
      if (typeof sanitizedData[key] !== "string") return;

      if (normalizeValue(sanitizedData[key]) === normalizedName) {
        sanitizedData[key] = "";
      }
    });
  }

  const contactItems = getPrimaryContactItems(sanitizedData);
  const socialItems = getSocialItems(sanitizedData);
  const experience = getActiveExperiences(sanitizedData);
  const education = getActiveEducation(sanitizedData);
  const skills = getSkills(sanitizedData);
  const languages = getLanguages(sanitizedData);
  const about = getAboutText(sanitizedData);
  const objective = getObjectiveText(sanitizedData);
  const summary = getSummaryText(sanitizedData);
  const projects = getProjects(sanitizedData);

  return {
    ...sanitizedData,
    summary: about || summary,
    objective,
    experience,
    education,
    skills,
    languages,
    projects,
    contactItems,
    socialItems,
    contactsByKey: new Map([...contactItems, ...socialItems].map((item) => [item.key, item.value])),
  };
}

function resolveFieldValue(node, data, root) {
  const field = normalizeValue(node.getAttribute("data-field"));
  if (!field) return { field, value: "", valid: false };

  if (!KNOWN_FIELDS.has(field)) {
    return { field, value: "", valid: false };
  }

  if (field === "skill") {
    const index = getNodeCollectionIndex(node, "[data-field='skill']");
    return { field, value: data.skills[index] || "", valid: true };
  }

  if (field === "language") {
    const index = getNodeCollectionIndex(node, "[data-field='language']");
    return { field, value: data.languages[index] || "", valid: true };
  }

  if (field === "skillCount") {
    return { field, value: data.skills.length > 0 ? String(data.skills.length) : "", valid: true };
  }

  if (field === "languageCount") {
    return {
      field,
      value: data.languages.length > 0 ? String(data.languages.length) : "",
      valid: true,
    };
  }

  if (field === "projectCount") {
    const projectCount = getProjects(data).length || data.experience.length || 0;
    return { field, value: projectCount > 0 ? String(projectCount) : "", valid: true };
  }

  if (field === "achievements") {
    return { field, value: normalizeValue(stripHtml(node.textContent || "")), valid: true };
  }

  const experienceIndex = getExperienceIndex(node, root);
  if (experienceIndex !== -1) {
    const experience = data.experience[experienceIndex] || {};

    if (field === "jobRole") return { field, value: experience.role || "", valid: true };
    if (field === "years") {
      return {
        field,
        value: formatDateRange(experience.startDate, experience.endDate, experience.years),
        valid: true,
      };
    }
    if (field === "description") {
      return { field, value: normalizeValue(stripHtml(node.textContent || "")), valid: true };
    }

    if (field === "technology") {
      const technologyIndex = getNodeCollectionIndex(node, "[data-field='technology']");
      return {
        field,
        value: getExperienceTechnologies(experience)[technologyIndex] || "",
        valid: true,
      };
    }

    return { field, value: experience[field] || "", valid: true };
  }

  const educationIndex = getEducationIndex(node, root);
  if (educationIndex !== -1) {
    const education = data.education[educationIndex] || {};

    if (field === "years") {
      return {
        field,
        value: formatDateRange(education.from, education.to, education.years),
        valid: true,
      };
    }

    if (field === "startDate") {
      return { field, value: education.from || education.startDate || "", valid: true };
    }

    if (field === "endDate") {
      return { field, value: education.to || education.endDate || "", valid: true };
    }

    return { field, value: education[field] || "", valid: true };
  }

  const projectIndex = getProjectIndex(node, root);
  if (projectIndex !== -1) {
    const project = data.projects[projectIndex] || {};

    if (field === "project") {
      return {
        field,
        value: project.project || project.title || project.name || "",
        valid: true,
      };
    }

    if (field === "description") {
      return { field, value: normalizeValue(stripHtml(node.textContent || "")), valid: true };
    }

    return { field, value: project[field] || "", valid: true };
  }

  if (data.contactsByKey.has(field)) {
    return { field, value: data.contactsByKey.get(field) || "", valid: true };
  }

  return { field, value: data[field] || "", valid: true };
}

function getMeaningfulText(node) {
  if (!node) return "";

  const clone = node.cloneNode(true);
  clone.querySelectorAll("[data-title], [data-title='true'], .cv-heading").forEach((heading) => {
    heading.remove();
  });

  return normalizeValue(stripHtml(clone.innerHTML || clone.textContent || ""));
}

function getSectionHeadingLabel(node) {
  if (!node) return "";

  const headingNode = node.querySelector("[data-title], [data-title='true'], .cv-heading");
  return normalizeValue(headingNode?.textContent || "");
}

function matchesForbiddenSectionLabel(label = "") {
  const normalizedLabel = normalizeValue(label).toLowerCase();
  if (!normalizedLabel) return false;

  return Array.from(FORBIDDEN_SECTION_LABELS).some((forbiddenLabel) => {
    const normalizedForbiddenLabel = normalizeValue(forbiddenLabel).toLowerCase();
    return (
      normalizedLabel === normalizedForbiddenLabel ||
      normalizedLabel.includes(normalizedForbiddenLabel) ||
      normalizedForbiddenLabel.includes(normalizedLabel)
    );
  });
}

function hasMeaningfulRenderableContent(node) {
  if (!node || !isNodeVisible(node)) return false;

  if (node.matches?.("img, svg, canvas") && node.getAttribute("src")) {
    return true;
  }

  if (hasRenderableText(getMeaningfulText(node))) {
    return true;
  }

  return Array.from(node.children || []).some((child) => hasMeaningfulRenderableContent(child));
}

function pruneEmptyFields(root, data) {
  const invalidBindings = [];

  Array.from(root.querySelectorAll("[data-field]")).forEach((node) => {
    showNode(node);

    const binding = resolveFieldValue(node, data, root);
    const renderedText = normalizeValue(stripHtml(node.innerHTML || node.textContent || ""));

    if (!binding.valid) {
      invalidBindings.push(binding.field || "(missing)");
      if (!hasRenderableText(renderedText)) {
        hideNode(node, "invalid-binding");
        hideSingleFieldShell(node, "invalid-binding-shell");
      }
      return;
    }

    if (FORBIDDEN_FIELDS.has(binding.field)) {
      hideNode(node, "forbidden-field");
      hideSingleFieldShell(node, "forbidden-field-shell");
      return;
    }

    if (!hasRenderableText(binding.value)) {
      if (
        PRESERVE_RENDERED_FALLBACK_FIELDS.has(binding.field) &&
        hasRenderableText(renderedText)
      ) {
        return;
      }

      hideNode(node, "empty-field");
      hideSingleFieldShell(node, "empty-field-shell");
    }
  });

  return invalidBindings;
}

function scanInvalidBindings(root, data) {
  return Array.from(root.querySelectorAll("[data-field]"))
    .map((node) => resolveFieldValue(node, data, root))
    .filter((binding) => !binding.valid)
    .map((binding) => binding.field || "(missing)");
}

function pruneEmptyGroups(root) {
  const groups = Array.from(root.querySelectorAll("[data-cv-group='true']")).reverse();

  groups.forEach((node) => {
    if (!hasMeaningfulRenderableContent(node)) {
      hideNode(node, "empty-group");
    }
  });
}

function pruneEmptySections(root) {
  const sections = Array.from(root.querySelectorAll("[data-cv-section='true']")).reverse();

  sections.forEach((node) => {
    const kind = normalizeValue(node.getAttribute("data-cv-kind"));
    const headingLabel = getSectionHeadingLabel(node).toLowerCase();

    if (kind === "header") return;
    if (FORBIDDEN_SECTION_KINDS.has(kind) || matchesForbiddenSectionLabel(headingLabel)) {
      hideNode(node, "forbidden-section");
      return;
    }
    if (!hasMeaningfulRenderableContent(node)) {
      hideNode(node, "empty-section");
    }
  });
}

function removeDuplicateSingletonSections(root) {
  const seenKinds = new Set();

  Array.from(root.querySelectorAll("[data-cv-section='true']")).forEach((section) => {
    if (!isNodeVisible(section)) return;

    const kind = normalizeValue(section.getAttribute("data-cv-kind"));
    if (!DUPLICATE_SECTION_KINDS.has(kind)) return;
    if (!hasMeaningfulRenderableContent(section)) return;
    if (
      section.parentElement?.closest?.(
        `[data-cv-section='true'][data-cv-kind='${kind}']`,
      )
    ) {
      return;
    }

    if (seenKinds.has(kind)) {
      hideNode(section, "duplicate-section");
      return;
    }

    seenKinds.add(kind);
  });
}

function reorderSiblingSections(root) {
  void root;
}

function applySectionDensity(root) {
  const visibleSections = Array.from(root.querySelectorAll("[data-cv-section='true']"))
    .filter((section) => isNodeVisible(section));

  visibleSections.forEach((section) => {
    const visibleFieldCount = Array.from(section.querySelectorAll("[data-field]"))
      .filter((node) => isNodeVisible(node))
      .length;
    const visibleGroupCount = Array.from(section.querySelectorAll("[data-cv-group='true']"))
      .filter((node) => isNodeVisible(node))
      .length;
    const score = visibleFieldCount + visibleGroupCount;

    const density =
      score <= 2 ? "tight" :
      score <= 6 ? "balanced" :
      "relaxed";

    section.setAttribute("data-cv-density", density);
  });
}

function collectMissingFieldCandidates(root) {
  return Array.from(root.querySelectorAll("p, li, span, div"))
    .filter((node) => isNodeVisible(node))
    .filter((node) => node.children.length === 0)
    .filter((node) => !node.closest("[data-field]"))
    .filter((node) => !node.closest("[data-title], [data-title='true'], .cv-heading"))
    .map((node) => normalizeValue(node.textContent || ""))
    .filter((text) => /\s/.test(text))
    .filter((text) => /[@]|\d{4}|[a-z]{3,}\s+[a-z]{3,}/i.test(text))
    .slice(0, 20);
}

export function validateTemplateStructure(root, data) {
  if (!root) {
    return {
      duplicateSections: [],
      invalidBindings: [],
      missingDataFieldContent: [],
    };
  }

  const snapshot = getRenderableSnapshot(data || {});
  const invalidBindings = scanInvalidBindings(root, snapshot);

  const sectionKinds = Array.from(root.querySelectorAll("[data-cv-section='true']"))
    .map((node) => normalizeValue(node.getAttribute("data-cv-kind")))
    .filter((kind) => DUPLICATE_SECTION_KINDS.has(kind));

  const duplicateSections = Array.from(new Set(
    sectionKinds.filter(
      (kind, index) => sectionKinds.indexOf(kind) !== index,
    ),
  ));

  return {
    duplicateSections,
    invalidBindings: Array.from(new Set(invalidBindings.filter(Boolean))),
    missingDataFieldContent: collectMissingFieldCandidates(root),
  };
}

export function applyRenderValidation(root, data) {
  if (!root) return null;

  const snapshot = getRenderableSnapshot(data || {});
  const invalidBindings = pruneEmptyFields(root, snapshot);
  pruneEmptyGroups(root);
  pruneEmptySections(root);
  removeDuplicateSingletonSections(root);
  reorderSiblingSections(root);
  pruneEmptySections(root);
  applySectionDensity(root);

  return {
    invalidBindings: Array.from(new Set(invalidBindings.filter(Boolean))),
  };
}
